import React, { useState, useEffect, useCallback } from 'react';
import { Outlet, Navigate, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../contexts/AuthContext';
import { API } from '@/lib/apiBase';
import { Sidebar } from './Sidebar';
import { MaintenancePage } from '../MaintenancePage';
import { AlertTriangle } from 'lucide-react';
import { HelpCenterPanel } from './HelpCenterPanel';
import VersionWhatsNewModal from '../VersionWhatsNewModal';
import { countTutorialsNeedingAttention } from './helpCenterPending';
import { useIsMobile } from '../../hooks/useIsMobile';

export const Layout = () => {
  const { user, loading, maintenanceMode, isAdmin, logout, token } = useAuth();
  const isMobile = useIsMobile();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [helpCenterOpen, setHelpCenterOpen] = useState(false);
  const [helpPendingCount, setHelpPendingCount] = useState(0);
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();

  const refreshHelpPending = useCallback(async (themesHint) => {
    if (!token) {
      setHelpPendingCount(0);
      return;
    }
    if (Array.isArray(themesHint)) {
      setHelpPendingCount(countTutorialsNeedingAttention(themesHint));
      return;
    }
    try {
      const res = await axios.get(`${API}/help/video-themes`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setHelpPendingCount(countTutorialsNeedingAttention(res.data));
    } catch {
      setHelpPendingCount(0);
    }
  }, [token]);

  useEffect(() => {
    if (!token) {
      setHelpPendingCount(0);
      return;
    }
    if (helpCenterOpen) return;
    refreshHelpPending();
  }, [token, helpCenterOpen, refreshHelpPending]);

  // Détecter si un token SSO est présent dans l'URL (envoyé par la Centrale)
  useEffect(() => {
    // La Centrale peut utiliser différents noms de paramètres
    const ssoToken = searchParams.get('token') 
      || searchParams.get('sso_token') 
      || searchParams.get('jwt') 
      || searchParams.get('auth_token')
      || searchParams.get('t');
    
    // Debug: afficher tous les paramètres reçus
    console.log('Layout SSO Debug - URL params:', Object.fromEntries(searchParams.entries()));
    
    if (ssoToken) {
      console.log('Layout SSO Debug - Token found, redirecting to /sso');
      // Rediriger vers /sso avec le token pour traitement
      navigate(`/sso?token=${encodeURIComponent(ssoToken)}`, { replace: true });
    }
  }, [searchParams, navigate]);

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-background">
        <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) {
    // Vérifier si un token est dans l'URL avant de rediriger vers login
    const ssoToken = searchParams.get('token') 
      || searchParams.get('sso_token') 
      || searchParams.get('jwt') 
      || searchParams.get('auth_token')
      || searchParams.get('t');
    if (ssoToken) {
      return <Navigate to={`/sso?token=${encodeURIComponent(ssoToken)}`} replace />;
    }
    return <Navigate to="/login" replace />;
  }

  // Maintenance mode: block non-admins
  if (maintenanceMode?.is_active && !isAdmin) {
    const handleAdminAccess = () => {
      logout();
      navigate('/login');
    };
    
    return (
      <MaintenancePage 
        maintenanceMode={maintenanceMode} 
        onAdminAccess={handleAdminAccess}
      />
    );
  }

  return (
    <div className="flex h-screen min-h-0 overflow-hidden bg-background">
      <Sidebar
        collapsed={sidebarCollapsed}
        setCollapsed={setSidebarCollapsed}
        setHelpCenterOpen={setHelpCenterOpen}
        helpPendingCount={helpPendingCount}
        isMobile={isMobile}
      />

      <div className="flex min-h-0 min-w-0 flex-1 overflow-hidden">
        <main className="min-h-0 min-w-0 flex-1 overflow-y-auto overflow-x-hidden overscroll-y-contain">
          {/* Maintenance Banner (for admins only when maintenance is active) */}
          {maintenanceMode?.is_active && isAdmin && (
            <div
              className="maintenance-banner px-4 py-3 flex items-center justify-center gap-3"
              data-testid="maintenance-banner"
            >
              <AlertTriangle className="w-5 h-5 text-orange-500" />
              <span className="text-orange-500 font-medium">
                Mode maintenance actif - Seuls les administrateurs ont accès
              </span>
              {maintenanceMode.reason && (
                <span className="text-orange-400 text-sm">
                  | {maintenanceMode.reason}
                </span>
              )}
              {maintenanceMode.estimated_end && (
                <span className="text-orange-400 text-sm">
                  | Fin estimée: {new Date(maintenanceMode.estimated_end).toLocaleString('fr-FR')}
                </span>
              )}
            </div>
          )}

          <div className="p-6 md:p-8 lg:p-10">
            <Outlet />
          </div>
        </main>

        {helpCenterOpen && (
          <HelpCenterPanel
            onClose={() => setHelpCenterOpen(false)}
            onHelpPendingRefresh={refreshHelpPending}
          />
        )}
      </div>
      <VersionWhatsNewModal />
    </div>
  );
};
